import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listAdminCourses, statusPill, formatPrice } from "@/lib/courses";
import { listBooks } from "@/lib/books";
import { adminFetchCourseAudit } from "@/lib/courseAdmin";
import { BookOpenText, Loader2, Pencil, BookText, History } from "lucide-react";

const STATUSES = ["all", "draft", "published", "archived"];

export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [bookCounts, setBookCounts] = useState({});
  const [auditFor, setAuditFor] = useState(null); // course row
  const [auditEntries, setAuditEntries] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    listAdminCourses(status !== "all" ? { status } : {})
      .then(async (rows) => {
        setCourses(rows);
        // Fan out book lookups (cheap — collection is small in MVP demo).
        const entries = await Promise.all(
          rows.map(async (c) => {
            try {
              const books = await listBooks(c.id);
              return [c.id, books.filter((b) => b.status === "done").length];
            } catch {
              return [c.id, 0];
            }
          })
        );
        setBookCounts(Object.fromEntries(entries));
      })
      .finally(() => setLoading(false));
  }, [status]);

  const openAudit = (course) => {
    setAuditFor(course);
    setAuditEntries(null);
    setAuditLoading(true);
    adminFetchCourseAudit(course.id)
      .then((r) => setAuditEntries(r.entries || []))
      .catch(() => setAuditEntries([]))
      .finally(() => setAuditLoading(false));
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="admin-courses">
      <DashboardHeader subtitle="Admin · All courses" />
      <main className="mx-auto max-w-6xl px-6 py-8 space-y-6">
        <BackButton to="/admin" />
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Courses across teachers</h1>
            <p className="text-sm text-stone-500">
              Read-only view of every course in KKS.
            </p>
          </div>
          <div className="flex gap-2">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40" data-testid="admin-courses-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s === "all" ? "All statuses" : s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="text-base">
              {courses.length} course{courses.length === 1 ? "" : "s"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-12 text-center text-stone-500">
                <Loader2 className="mx-auto h-5 w-5 animate-spin mb-2" /> Loading…
              </div>
            ) : (
              courses.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between gap-3 px-4 py-3 border-b border-stone-100 last:border-b-0"
                  data-testid={`admin-course-row-${c.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{c.title}</div>
                    <div className="text-xs text-stone-500 truncate">
                      Owner: {c.owner_teacher_id.slice(0, 8)}… ·{" "}
                      {c.chapters.length} chapter{c.chapters.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline">{c.level}</Badge>
                    <Badge variant="outline" className={statusPill(c.status)}>
                      {c.status}
                    </Badge>
                    <Badge variant="outline">{formatPrice(c.pricing)}</Badge>
                    {bookCounts[c.id] > 0 && (
                      <Badge
                        variant="outline"
                        className="border-amber-300 text-amber-800 bg-amber-50"
                        data-testid={`has-book-pill-${c.id}`}
                      >
                        <BookOpenText className="h-3 w-3 mr-1" />
                        Has book ({bookCounts[c.id]})
                      </Badge>
                    )}
                    <Button
                      asChild
                      size="sm"
                      className="bg-stone-900 hover:bg-stone-800 text-white"
                      data-testid={`admin-edit-${c.id}`}
                    >
                      <Link to={`/teacher/courses/${c.id}/edit?as_admin=1`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                      </Link>
                    </Button>
                    <Button
                      asChild
                      size="sm"
                      variant="outline"
                      data-testid={`admin-edit-books-${c.id}`}
                    >
                      <Link to={`/teacher/courses/${c.id}/edit?as_admin=1&tab=book`}>
                        <BookText className="h-3.5 w-3.5 mr-1" /> Edit Books
                      </Link>
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openAudit(c)}
                      data-testid={`admin-audit-${c.id}`}
                      title="View audit log"
                    >
                      <History className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </main>

      <Dialog open={!!auditFor} onOpenChange={(v) => !v && setAuditFor(null)}>
        <DialogContent className="max-w-2xl" data-testid="audit-log-modal">
          <DialogHeader>
            <DialogTitle>Audit log — {auditFor?.title}</DialogTitle>
          </DialogHeader>
          {auditLoading ? (
            <div className="py-8 text-center text-stone-500">
              <Loader2 className="mx-auto h-5 w-5 animate-spin mb-2" /> Loading…
            </div>
          ) : !auditEntries || auditEntries.length === 0 ? (
            <div className="py-6 text-center text-stone-500" data-testid="audit-empty">
              No edits yet.
            </div>
          ) : (
            <ul
              className="divide-y divide-stone-100 max-h-[60vh] overflow-y-auto"
              data-testid="audit-entries"
            >
              {auditEntries.map((e) => (
                <li key={e.id} className="py-2 text-sm">
                  <div className="text-xs text-stone-500">
                    {new Date(e.at).toLocaleString()} ·{" "}
                    <b>{e.actor_name}</b> ({e.actor_role}) · {e.action}
                  </div>
                  <div className="text-stone-700">{e.summary}</div>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
